import * as calc from '@/app/order-calculations';
import flatMap from 'lodash/flatMap';
import Decimal from '@/utils/decimal';
import uniqBy from 'lodash/uniqBy';

// Calculation order:
// - orderLineAllowanceCharge amount
// - orderLine lineExtensionAmount
// - orderLine taxAmount
// - orderAllowanceCharge amounts
// - order lineExtensionAmount
// - order taxExclusiveAmount
// - order taxSubtotal amounts
// - order taxAmount
// - order taxInclusiveAmount
// - order payableAmount

export interface IOrder {
  taxAmount: Decimal;
  lineExtensionAmount: Decimal;
  taxExclusiveAmount: Decimal;
  taxInclusiveAmount: Decimal;
  payableAmount: Decimal;
  orderLines: IOrderLine[];
  orderAllowanceCharges: IOrderAllowanceCharge[];
}

export interface IOrderTaxSubtotal {
  taxableAmount: Decimal;
  taxPercent: Decimal;
  taxAmount: Decimal;
  taxInclusiveAmount: Decimal;
}

export interface IOrderLine {
  id: Decimal;
  quantity: Decimal;
  priceAmount: Decimal;
  lineExtensionAmount: Decimal;
  taxPercent: Decimal;
  taxAmount: Decimal;
  orderLineAllowanceCharge: IOrderLineAllowanceCharge | null;
  vatRateSymbol: string;
  _destroy: boolean;
}

export interface IOrderLineAllowanceCharge {
  id: Decimal;
  percent: Decimal;
  amount: Decimal;
}

export interface IOrderAllowanceCharge {
  id: Decimal;
  percent: Decimal;
  orderAllowanceChargeLines: IOrderAllowanceChargeLine[];
}

export interface IOrderAllowanceChargeLine {
  id: Decimal;
  amount: Decimal;
  baseAmount: Decimal;
  taxPercent: Decimal;
  _destroy: boolean;
}

// Order line calculations

export const orderLineLineExtensionAmount = (
  line: IOrderLine,
) => calc.orderLineLineExtensionAmount(
  line.quantity,
  line.priceAmount,
  line.orderLineAllowanceCharge ? line.orderLineAllowanceCharge.amount : new Decimal(0),
);

export const orderLineTaxAmount = (
  line: IOrderLine,
) => calc.orderLineTaxAmount(
  line.lineExtensionAmount,
  line.taxPercent,
);

// Order line allowance charge calculations

export const orderLineAllowanceChargeAmount = (
  ac: IOrderLineAllowanceCharge,
  line: IOrderLine,
) => calc.orderLineAllowanceChargeAmount(
  line.quantity,
  line.priceAmount,
  ac.percent,
);

// Order allowance charge calculations

interface ILineSumsByVatRateSymbol {
  [vatRateSymbol: string]: {
    vatRateSymbol: string;
    taxPercent: Decimal;
    amount: Decimal;
    baseAmount: Decimal;
  };
}

export const orderAllowanceChargeLineAmounts = (
  order: IOrder,
  ac: IOrderAllowanceCharge,
) => {
  const lineSums: ILineSumsByVatRateSymbol = order
    .orderLines
    .filter(line => !line._destroy)
    .reduce(
      (sums, line) => {
        if (!sums[line.vatRateSymbol]) {
          sums[line.vatRateSymbol] = {
            vatRateSymbol: line.vatRateSymbol,
            taxPercent: line.taxPercent,
            amount: new Decimal(0),
            baseAmount: new Decimal(0),
          };
        }

        sums[line.vatRateSymbol].baseAmount = sums[line.vatRateSymbol].baseAmount.add(line.lineExtensionAmount).toDecimalPlaces(2);

        return sums;
      },
      {} as ILineSumsByVatRateSymbol,
    );

  return Object.values(lineSums).map(sum => {
    sum.amount = calc.orderAllowanceChargeLineAmount(
      sum.baseAmount,
      ac.percent,
    );
    return sum;
  });
};

// Order calculations

export const orderTaxSubtotals = (
  order: IOrder,
): IOrderTaxSubtotal[] => {
  const taxPercents = uniqBy(
    order.orderLines.filter(line => !line._destroy).map(line => line.taxPercent),
    taxPercent => taxPercent.toString(),
  );

  return taxPercents.map(taxPercent => {
    const lineExtensionAmounts = order.orderLines
      .filter(line => !line._destroy && line.taxPercent.equals(taxPercent))
      .map(line => line.lineExtensionAmount);

    const allowanceChargeAmounts = flatMap(order.orderAllowanceCharges,
      ac =>
        ac.orderAllowanceChargeLines
          .filter(line => !line._destroy && line.taxPercent.equals(taxPercent))
          .map(line => line.amount),
    );

    const taxableAmount = calc.orderTaxSubtotalTaxableAmount(
      lineExtensionAmounts,
      allowanceChargeAmounts,
    );

    const taxAmount = calc.orderTaxSubtotalTaxAmount(
      taxableAmount,
      taxPercent,
    );

    const taxInclusiveAmount = calc.orderTaxSubtotalTaxInclusiveAmount(
      taxableAmount,
      taxAmount,
    );

    return {
      taxPercent,
      taxableAmount,
      taxAmount,
      taxInclusiveAmount,
    };
  });
};

export const orderTaxAmount = (
  taxSubtotals: IOrderTaxSubtotal[],
) => calc.orderTaxAmount(
  taxSubtotals.map(subtotal => subtotal.taxAmount),
);

export const orderLineExtensionAmount = (
  order: IOrder,
) => calc.orderLineExtensionAmount(
  order
    .orderLines
    .filter(line => !line._destroy)
    .map(line => line.lineExtensionAmount),
);

export const orderTaxExclusiveAmount = (
  order: IOrder,
) => calc.orderTaxExclusiveAmount(
  order.lineExtensionAmount,
  flatMap(order.orderAllowanceCharges,
    ac => ac
      .orderAllowanceChargeLines
      .filter(acl => !acl._destroy)
      .map(acl => acl.amount),
  ),
);

export const orderTaxInclusiveAmount = (
  order: IOrder,
) => calc.orderTaxInclusiveAmount(
  order.taxExclusiveAmount,
  order.taxAmount,
);

export const orderPayableAmount = (
  order: IOrder,
) => calc.orderPayableAmount(
  order.taxInclusiveAmount,
);
